
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Edit, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Trip } from '@/types/itinerary';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface TripHeaderProps {
  trip: Trip;
  isAuthorized: boolean;
}

const TripHeader: React.FC<TripHeaderProps> = ({ trip, isAuthorized }) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(trip.name);
  const [editedDescription, setEditedDescription] = useState(trip.description || '');

  const handleSaveTitle = async () => {
    if (!trip.id || !isAuthorized) return;

    try {
      // Don't update if the content didn't change
      if (editedTitle === trip.name && editedDescription === (trip.description || '')) {
        setIsEditingTitle(false);
        return;
      }

      const { error } = await supabase
        .from('trips')
        .update({ 
          name: editedTitle,
          description: editedDescription 
        })
        .eq('id', trip.id);

      if (error) throw error;

      // Update UI without full refresh
      trip.name = editedTitle;
      trip.description = editedDescription;
      
      setIsEditingTitle(false);
      toast.success("Trip details updated successfully");
    } catch (error) {
      console.error("Error updating trip:", error);
      toast.error("Failed to update trip details");
    }
  };

  return (
    <>
      <div className="mb-6 flex items-center">
        <Link to="/trips" className="flex items-center text-[#0A3D62] hover:text-glowingTeal mr-4">
          <ArrowLeft size={20} className="mr-1" />
          <span>Back to Trips</span>
        </Link>
      </div>
      
      <div className="mb-8">
        {isEditingTitle ? (
          <div className="space-y-3">
            <Input 
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              className="text-2xl font-bold border-[#0A3D62]/30 focus-visible:ring-purple-DEFAULT" 
              placeholder="Trip name"
              maxLength={100}
            />
            <Textarea 
              value={editedDescription || ''}
              onChange={(e) => setEditedDescription(e.target.value)}
              className="w-full border-[#0A3D62]/30 focus-visible:ring-purple-DEFAULT"
              placeholder="Trip description (optional)"
              maxLength={500}
            />
            <button 
              onClick={handleSaveTitle}
              className="inline-flex items-center text-sm text-white bg-glowingTeal hover:bg-glowingTeal/90 px-3 py-1 rounded"
            >
              <Check className="h-4 w-4 mr-1" />
              Save changes
            </button>
          </div>
        ) : (
          <div className="group relative">
            <div>
              <h1 className="text-3xl font-bold text-[#0A3D62] font-poppins">{trip.name}</h1>
              {trip.description && <p className="text-gray-600 mt-1">{trip.description}</p>}
              <p className="text-gray-600 mt-1">{trip.destination} • {new Date(trip.start_date).toLocaleDateString()} - {new Date(trip.end_date).toLocaleDateString()}</p>
            </div>
            {isAuthorized && (
              <button 
                onClick={() => setIsEditingTitle(true)} 
                className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gray-100 hover:bg-gray-200 p-1 rounded"
                aria-label="Edit trip details"
              >
                <Edit className="h-4 w-4 text-gray-600" />
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default TripHeader;
